import sodium from "libsodium-wrappers-sumo";
import type { IdentityKeypair } from "./keys.js";

const MAX_SKIP = 1000;
const CHAIN_KEY_SEED = new Uint8Array([0x01]);
const MSG_KEY_SEED = new Uint8Array([0x02]);
const HEADER_KEY_SEED = new Uint8Array([0x03]);

export interface RatchetHeader {
  dhPublicKey: Uint8Array;
  previousChainLength: number;
  messageNumber: number;
}

export interface EncryptedMessage {
  header: RatchetHeader;
  headerSignature: Uint8Array;
  ciphertext: Uint8Array;
  nonce: Uint8Array;
  /** V2 header encryption fields (present when envelope_version = "2.0.0") */
  envelopeVersion?: string;
  encryptedHeader?: Uint8Array;
  headerNonce?: Uint8Array;
}

interface ChainState {
  chainKey: Uint8Array;
  messageNumber: number;
}

interface SkippedKey {
  dhPub: string; // hex of the DH public key
  n: number; // message number
  messageKey: Uint8Array;
  headerKey?: Uint8Array; // present for v2 out-of-order messages
}

interface RatchetState {
  rootKey: Uint8Array;
  sendingChain: ChainState | null;
  receivingChain: ChainState | null;
  dhSendingKeypair: { publicKey: Uint8Array; privateKey: Uint8Array };
  dhReceivingPublicKey: Uint8Array | null;
  identityKeypair: { publicKey: Uint8Array; privateKey: Uint8Array };
  peerIdentityPublicKey: Uint8Array | null;
  previousSendingChainLength: number;
  skippedKeys: SkippedKey[];
}

function kdfChainKey(chainKey: Uint8Array): {
  nextChainKey: Uint8Array;
  messageKey: Uint8Array;
  headerKey: Uint8Array;
} {
  const nextChainKey = sodium.crypto_generichash(32, chainKey, CHAIN_KEY_SEED);
  const messageKey = sodium.crypto_generichash(32, chainKey, MSG_KEY_SEED);
  const headerKey = sodium.crypto_generichash(32, chainKey, HEADER_KEY_SEED);
  return { nextChainKey, messageKey, headerKey };
}

function kdfRootKey(
  rootKey: Uint8Array,
  dhOutput: Uint8Array,
): { newRootKey: Uint8Array; newChainKey: Uint8Array } {
  // Derive 64 bytes: first 32 = new root key, second 32 = new chain key
  const derived = sodium.crypto_generichash(
    64,
    new Uint8Array([...rootKey, ...dhOutput]),
  );
  return {
    newRootKey: derived.slice(0, 32),
    newChainKey: derived.slice(32, 64),
  };
}

function generateDHKeypair(): {
  publicKey: Uint8Array;
  privateKey: Uint8Array;
} {
  const kp = sodium.crypto_box_keypair();
  return { publicKey: kp.publicKey, privateKey: kp.privateKey };
}

function dh(
  myPrivate: Uint8Array,
  theirPublic: Uint8Array,
): Uint8Array {
  return sodium.crypto_scalarmult(myPrivate, theirPublic);
}

function serializeHeader(header: RatchetHeader): Uint8Array {
  const dhPubHex = sodium.to_hex(header.dhPublicKey);
  const json = JSON.stringify({
    dh: dhPubHex,
    pcl: header.previousChainLength,
    n: header.messageNumber,
  });
  return sodium.from_string(json);
}

function signHeader(
  header: RatchetHeader,
  identityPrivateKey: Uint8Array,
): Uint8Array {
  const serialized = serializeHeader(header);
  return sodium.crypto_sign_detached(serialized, identityPrivateKey);
}

function verifyHeaderSignature(
  header: RatchetHeader,
  signature: Uint8Array,
  identityPublicKey: Uint8Array,
): boolean {
  const serialized = serializeHeader(header);
  return sodium.crypto_sign_verify_detached(
    signature,
    serialized,
    identityPublicKey,
  );
}

export class DoubleRatchet {
  private state: RatchetState;

  private constructor(state: RatchetState) {
    this.state = state;
  }

  static initSender(
    sharedSecret: Uint8Array,
    identityKeypair: IdentityKeypair,
    peerIdentityPublicKey?: Uint8Array,
  ): DoubleRatchet {
    const dhKeypair = generateDHKeypair();

    return new DoubleRatchet({
      rootKey: sharedSecret,
      sendingChain: { chainKey: sharedSecret, messageNumber: 0 },
      receivingChain: null,
      dhSendingKeypair: dhKeypair,
      dhReceivingPublicKey: null,
      identityKeypair: {
        publicKey: identityKeypair.publicKey,
        privateKey: identityKeypair.privateKey,
      },
      peerIdentityPublicKey: peerIdentityPublicKey ?? null,
      previousSendingChainLength: 0,
      skippedKeys: [],
    });
  }

  static initReceiver(
    sharedSecret: Uint8Array,
    identityKeypair: IdentityKeypair,
    peerIdentityPublicKey?: Uint8Array,
  ): DoubleRatchet {
    const dhKeypair = generateDHKeypair();

    return new DoubleRatchet({
      rootKey: sharedSecret,
      sendingChain: null,
      receivingChain: { chainKey: sharedSecret, messageNumber: 0 },
      dhSendingKeypair: dhKeypair,
      dhReceivingPublicKey: null,
      identityKeypair: {
        publicKey: identityKeypair.publicKey,
        privateKey: identityKeypair.privateKey,
      },
      peerIdentityPublicKey: peerIdentityPublicKey ?? null,
      previousSendingChainLength: 0,
      skippedKeys: [],
    });
  }

  encrypt(plaintext: string, headerEncryption = false): EncryptedMessage {
    if (!this.state.sendingChain) {
      // Perform DH ratchet step for sending
      this.dhRatchetSend();
    }

    const chain = this.state.sendingChain!;
    const { nextChainKey, messageKey, headerKey } = kdfChainKey(chain.chainKey);

    const header: RatchetHeader = {
      dhPublicKey: this.state.dhSendingKeypair.publicKey,
      previousChainLength: this.state.previousSendingChainLength,
      messageNumber: chain.messageNumber,
    };

    const headerSignature = signHeader(
      header,
      this.state.identityKeypair.privateKey,
    );

    const nonce = sodium.randombytes_buf(
      sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES,
    );

    const plaintextBytes = sodium.from_string(plaintext);

    if (headerEncryption) {
      // V2: encrypt header, use encrypted header bytes as AD for message ciphertext
      const headerBytes = serializeHeader(header);
      const headerNonce = sodium.randombytes_buf(
        sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES,
      );
      const encryptedHeader = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
        headerBytes,
        null,
        null,
        headerNonce,
        headerKey,
      );

      // Use encrypted header as AD for message ciphertext
      const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
        plaintextBytes,
        encryptedHeader,
        null,
        nonce,
        messageKey,
      );

      chain.chainKey = nextChainKey;
      chain.messageNumber++;

      return {
        header,
        headerSignature,
        ciphertext,
        nonce,
        envelopeVersion: "2.0.0",
        encryptedHeader,
        headerNonce,
      };
    }

    // V1: plaintext header as AD (existing behavior)
    const ad = serializeHeader(header);
    const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
      plaintextBytes,
      ad,
      null,
      nonce,
      messageKey,
    );

    chain.chainKey = nextChainKey;
    chain.messageNumber++;

    return { header, headerSignature, ciphertext, nonce };
  }

  decrypt(message: EncryptedMessage): string {
    // #1009: verify the header BEFORE anything reads or mutates ratchet state.
    //
    // This used to sit after `skipMessages` and after the chain had already
    // been advanced. The header carries `dhPublicKey` and `messageNumber` —
    // the two fields that drive a DH ratchet step and a skip loop — so an
    // unsigned header could make this ratchet do real, irreversible work
    // before anyone asked whether the peer actually sent it. A header claiming
    // `messageNumber: 999999` was refused by MAX_SKIP, i.e. only AFTER the
    // skip loop had run.
    //
    // The signature covers the header alone, and verifying it needs nothing
    // but the peer's identity key, so there is no reason for it to run second.
    //
    // ⚠️ Still behind a null guard: a session constructed without a peer
    // identity key verifies nothing. Production passes one, but pre-existing
    // serialized sessions may not, and failing closed here would lock them
    // out. The commit-after-AEAD change below is what protects those.
    if (this.state.peerIdentityPublicKey) {
      if (!verifyHeaderSignature(message.header, message.headerSignature, this.state.peerIdentityPublicKey)) {
        throw new Error("Header signature verification failed");
      }
    }

    // V2 dispatch: if message has encryptedHeader, use v2 path
    const isV2 = message.envelopeVersion === "2.0.0" &&
      message.encryptedHeader != null &&
      message.headerNonce != null;

    // Try skipped keys first
    const skippedResult = this.trySkippedKeys(message, isV2);
    if (skippedResult !== null) {
      return skippedResult;
    }

    const headerDhHex = sodium.to_hex(message.header.dhPublicKey);
    const currentDhHex = this.state.dhReceivingPublicKey
      ? sodium.to_hex(this.state.dhReceivingPublicKey)
      : null;

    if (currentDhHex === null && this.state.receivingChain) {
      // First message ever received by the receiver side.
      // The receiving chain was initialized from the shared secret,
      // so just store the sender's DH key for future ratchet steps.
      this.state.dhReceivingPublicKey = message.header.dhPublicKey;
    } else if (currentDhHex === null && !this.state.receivingChain) {
      // Sender (initiator) receiving its first message from the receiver.
      if (message.header.messageNumber === 0) {
        try {
          const { messageKey: testKey, headerKey: testHeaderKey, nextChainKey } = kdfChainKey(
            this.state.rootKey,
          );

          let ad: Uint8Array;
          if (isV2) {
            // Decrypt header to verify, then use encryptedHeader as AD
            sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
              null,
              message.encryptedHeader!,
              null,
              message.headerNonce!,
              testHeaderKey,
            );
            ad = message.encryptedHeader!;
          } else {
            ad = serializeHeader(message.header);
          }

          const ptBytes =
            sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
              null,
              message.ciphertext,
              ad,
              message.nonce,
              testKey,
            );
          // rootKey worked — commit state
          this.state.dhReceivingPublicKey = message.header.dhPublicKey;
          this.state.receivingChain = {
            chainKey: nextChainKey,
            messageNumber: 1,
          };
          return sodium.to_string(ptBytes);
        } catch {
          // rootKey chain didn't match — fall through to DH ratchet
        }
      }
      // Case (b) or messageNumber > 0: full DH ratchet receive
      this.dhRatchetReceive(message.header.dhPublicKey);
    } else if (headerDhHex !== currentDhHex) {
      // DH public key changed (direction change in ongoing conversation).
      if (this.state.receivingChain && this.state.dhReceivingPublicKey) {
        this.skipMessages(
          this.state.receivingChain,
          message.header.previousChainLength,
          this.state.dhReceivingPublicKey,
          isV2,
        );
      }

      // DH ratchet step
      this.dhRatchetReceive(message.header.dhPublicKey);
    }

    // Skip ahead to the message number in the current chain
    this.skipMessages(
      this.state.receivingChain!,
      message.header.messageNumber,
      message.header.dhPublicKey,
      isV2,
    );

    const chain = this.state.receivingChain!;
    // #1009: DERIVE the keys, but do not commit the advance yet.
    //
    // This used to be `chain.chainKey = nextChainKey; chain.messageNumber++`
    // right here — before the AEAD tag was checked. So a relay (the AgentVault
    // server, untrusted by the threat model) could flip ONE BYTE of a genuine
    // ciphertext, watch the chain advance to n+1 and the decrypt throw, then
    // deliver the real message n. `skipMessages(until = n)` was by then a
    // no-op, the key for n+1 was derived instead, and that message and every
    // one after it failed. The session was dead, silently and permanently, at
    // a cost to the attacker of one byte. No plaintext leaked — this was a
    // denial of service, not a confidentiality break.
    //
    // Nothing rewound the chain because nothing knew it needed to. So the
    // advance now happens where the answer is known: after the tag verifies.
    const { nextChainKey, messageKey, headerKey } = kdfChainKey(chain.chainKey);

    let plaintextBytes: Uint8Array;

    if (isV2) {
      // V2: decrypt header to verify integrity, use encryptedHeader as AD
      try {
        sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
          null,
          message.encryptedHeader!,
          null,
          message.headerNonce!,
          headerKey,
        );
      } catch {
        throw new Error("V2 header decryption failed");
      }

      try {
        plaintextBytes = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
          null,
          message.ciphertext,
          message.encryptedHeader!,
          message.nonce,
          messageKey,
        );
      } catch {
        throw new Error("V2 decryption failed: ciphertext tampered or wrong key");
      }
    } else {
      // V1: plaintext header as AD
      const ad = serializeHeader(message.header);

      try {
        plaintextBytes = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
          null,
          message.ciphertext,
          ad,
          message.nonce,
          messageKey,
        );
      } catch {
        throw new Error("Decryption failed: ciphertext tampered or wrong key");
      }
    }

    // Authenticated — the peer signed this header and holds the key that
    // produced this tag. NOW consume the message number, which is what makes
    // forward secrecy work and what makes a replay fail.
    chain.chainKey = nextChainKey;
    chain.messageNumber++;

    return sodium.to_string(plaintextBytes);
  }

  private dhRatchetReceive(theirDhPublic: Uint8Array): void {
    this.state.previousSendingChainLength =
      this.state.sendingChain?.messageNumber ?? 0;
    this.state.dhReceivingPublicKey = theirDhPublic;

    // Derive new root key + receiving chain key
    const dhOutput = dh(
      this.state.dhSendingKeypair.privateKey,
      theirDhPublic,
    );
    const { newRootKey, newChainKey } = kdfRootKey(
      this.state.rootKey,
      dhOutput,
    );
    this.state.rootKey = newRootKey;
    this.state.receivingChain = { chainKey: newChainKey, messageNumber: 0 };

    // Generate new DH keypair for sending
    this.state.dhSendingKeypair = generateDHKeypair();

    // Derive new sending chain
    const dhOutput2 = dh(
      this.state.dhSendingKeypair.privateKey,
      theirDhPublic,
    );
    const { newRootKey: newRootKey2, newChainKey: newChainKey2 } = kdfRootKey(
      this.state.rootKey,
      dhOutput2,
    );
    this.state.rootKey = newRootKey2;
    this.state.sendingChain = { chainKey: newChainKey2, messageNumber: 0 };
  }

  private dhRatchetSend(): void {
    if (!this.state.dhReceivingPublicKey) {
      // No receiving key yet - create initial sending chain from root key
      this.state.sendingChain = {
        chainKey: this.state.rootKey,
        messageNumber: 0,
      };
      return;
    }

    this.state.previousSendingChainLength =
      this.state.sendingChain?.messageNumber ?? 0;

    // Generate new DH keypair
    this.state.dhSendingKeypair = generateDHKeypair();

    const dhOutput = dh(
      this.state.dhSendingKeypair.privateKey,
      this.state.dhReceivingPublicKey,
    );
    const { newRootKey, newChainKey } = kdfRootKey(
      this.state.rootKey,
      dhOutput,
    );
    this.state.rootKey = newRootKey;
    this.state.sendingChain = { chainKey: newChainKey, messageNumber: 0 };
  }

  private skipMessages(
    chain: ChainState,
    until: number,
    dhPublicKey: Uint8Array,
    storeHeaderKey = false,
  ): void {
    if (until - chain.messageNumber > MAX_SKIP) {
      throw new Error("Too many skipped messages");
    }

    const dhPubHex = sodium.to_hex(dhPublicKey);

    while (chain.messageNumber < until) {
      const { nextChainKey, messageKey, headerKey } = kdfChainKey(chain.chainKey);
      const sk: SkippedKey = {
        dhPub: dhPubHex,
        n: chain.messageNumber,
        messageKey,
      };
      if (storeHeaderKey) {
        sk.headerKey = headerKey;
      }
      this.state.skippedKeys.push(sk);
      chain.chainKey = nextChainKey;
      chain.messageNumber++;
    }

    // Trim skipped keys if too many — securely erase discarded key material
    while (this.state.skippedKeys.length > MAX_SKIP) {
      const discarded = this.state.skippedKeys.shift()!;
      sodium.memzero(discarded.messageKey);
      if (discarded.headerKey) {
        sodium.memzero(discarded.headerKey);
      }
    }
  }

  private trySkippedKeys(message: EncryptedMessage, isV2 = false): string | null {
    const dhPubHex = sodium.to_hex(message.header.dhPublicKey);
    const idx = this.state.skippedKeys.findIndex(
      (sk) => sk.dhPub === dhPubHex && sk.n === message.header.messageNumber,
    );

    if (idx === -1) {
      return null;
    }

    const skipped = this.state.skippedKeys[idx];
    this.state.skippedKeys.splice(idx, 1);

    // Securely erase key material after use (deferred until after decryption below)
    const eraseSkippedKey = () => {
      sodium.memzero(skipped.messageKey);
      if (skipped.headerKey) {
        sodium.memzero(skipped.headerKey);
      }
    };

    try {
      if (isV2 && skipped.headerKey && message.encryptedHeader && message.headerNonce) {
        // V2: verify encrypted header, use as AD
        try {
          sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
            null,
            message.encryptedHeader,
            null,
            message.headerNonce,
            skipped.headerKey,
          );
        } catch {
          throw new Error("V2 header decryption failed with skipped key");
        }

        try {
          const plaintextBytes =
            sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
              null,
              message.ciphertext,
              message.encryptedHeader,
              message.nonce,
              skipped.messageKey,
            );
          return sodium.to_string(plaintextBytes);
        } catch {
          throw new Error("V2 decryption failed with skipped key");
        }
      }

      // V1: plaintext header as AD
      const ad = serializeHeader(message.header);

      try {
        const plaintextBytes =
          sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
            null,
            message.ciphertext,
            ad,
            message.nonce,
            skipped.messageKey,
          );
        return sodium.to_string(plaintextBytes);
      } catch {
        throw new Error("Decryption failed with skipped key");
      }
    } finally {
      eraseSkippedKey();
    }
  }

  serialize(): string {
    const s = this.state;
    return JSON.stringify({
      version: 1,
      rootKey: sodium.to_hex(s.rootKey),
      sendingChain: s.sendingChain
        ? {
            chainKey: sodium.to_hex(s.sendingChain.chainKey),
            messageNumber: s.sendingChain.messageNumber,
          }
        : null,
      receivingChain: s.receivingChain
        ? {
            chainKey: sodium.to_hex(s.receivingChain.chainKey),
            messageNumber: s.receivingChain.messageNumber,
          }
        : null,
      dhSendingKeypair: {
        publicKey: sodium.to_hex(s.dhSendingKeypair.publicKey),
        privateKey: sodium.to_hex(s.dhSendingKeypair.privateKey),
      },
      dhReceivingPublicKey: s.dhReceivingPublicKey
        ? sodium.to_hex(s.dhReceivingPublicKey)
        : null,
      identityKeypair: {
        publicKey: sodium.to_hex(s.identityKeypair.publicKey),
        privateKey: sodium.to_hex(s.identityKeypair.privateKey),
      },
      peerIdentityPublicKey: s.peerIdentityPublicKey ? sodium.to_hex(s.peerIdentityPublicKey) : null,
      previousSendingChainLength: s.previousSendingChainLength,
      skippedKeys: s.skippedKeys.map((sk) => ({
        dhPub: sk.dhPub,
        n: sk.n,
        messageKey: sodium.to_hex(sk.messageKey),
        ...(sk.headerKey ? { headerKey: sodium.to_hex(sk.headerKey) } : {}),
      })),
    });
  }

  static deserialize(json: string): DoubleRatchet {
    let d: Record<string, unknown>;
    try {
      d = JSON.parse(json) as Record<string, unknown>;
    } catch {
      throw new Error("Ratchet state: corrupt JSON");
    }

    // Version gate
    if (d.version !== undefined && d.version !== 1) {
      throw new Error(
        `Ratchet state version ${d.version} not supported`,
      );
    }

    // Required field validation
    if (typeof d.rootKey !== "string") {
      throw new Error("Ratchet state: missing required field rootKey");
    }
    const dhSend = d.dhSendingKeypair as
      | { publicKey?: string; privateKey?: string }
      | undefined;
    if (
      !dhSend ||
      typeof dhSend.publicKey !== "string" ||
      typeof dhSend.privateKey !== "string"
    ) {
      throw new Error(
        "Ratchet state: missing required field dhSendingKeypair",
      );
    }
    const idKp = d.identityKeypair as
      | { publicKey?: string; privateKey?: string }
      | undefined;
    if (
      !idKp ||
      typeof idKp.publicKey !== "string" ||
      typeof idKp.privateKey !== "string"
    ) {
      throw new Error(
        "Ratchet state: missing required field identityKeypair",
      );
    }

    try {
      return new DoubleRatchet({
        rootKey: sodium.from_hex(d.rootKey as string),
        sendingChain: d.sendingChain
          ? {
              chainKey: sodium.from_hex(
                (d.sendingChain as { chainKey: string }).chainKey,
              ),
              messageNumber: (d.sendingChain as { messageNumber: number })
                .messageNumber,
            }
          : null,
        receivingChain: d.receivingChain
          ? {
              chainKey: sodium.from_hex(
                (d.receivingChain as { chainKey: string }).chainKey,
              ),
              messageNumber: (d.receivingChain as { messageNumber: number })
                .messageNumber,
            }
          : null,
        dhSendingKeypair: {
          publicKey: sodium.from_hex(dhSend.publicKey!),
          privateKey: sodium.from_hex(dhSend.privateKey!),
        },
        dhReceivingPublicKey: d.dhReceivingPublicKey
          ? sodium.from_hex(d.dhReceivingPublicKey as string)
          : null,
        identityKeypair: {
          publicKey: sodium.from_hex(idKp.publicKey!),
          privateKey: sodium.from_hex(idKp.privateKey!),
        },
        peerIdentityPublicKey: d.peerIdentityPublicKey
          ? sodium.from_hex(d.peerIdentityPublicKey as string)
          : null,
        previousSendingChainLength:
          d.previousSendingChainLength as number,
        skippedKeys: (
          d.skippedKeys as Array<{
            dhPub: string;
            n: number;
            messageKey: string;
            headerKey?: string;
          }>
        ).map((sk) => ({
          dhPub: sk.dhPub,
          n: sk.n,
          messageKey: sodium.from_hex(sk.messageKey),
          ...(sk.headerKey ? { headerKey: sodium.from_hex(sk.headerKey) } : {}),
        })),
      });
    } catch (err) {
      if (err instanceof Error && err.message.startsWith("Ratchet state")) {
        throw err;
      }
      throw new Error(
        `Ratchet state: corrupt hex data — ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }
}
